import { useMemo } from 'react';
import { DashboardRegistryItem, Registry, UserView, UserViewType } from '../types/user';
import { defaultViews } from '../support/config/tables';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../support/store';
import { updateRegistry } from '../support/slices/registrySlice';

import { RouteHistoryEntry } from '../support/slices/uiSlice';

const DEPLOYMENT = process.env.REACT_APP_BRANCH_NAME;

function isRegistryItem(obj: unknown): obj is DashboardRegistryItem {
  return (
    !!obj &&
    typeof obj === 'object' &&
    'id' in obj &&
    'tiles' in obj &&
    'name' in obj
  );
}

export const useUserRegistry = () => {
  const dispatch = useAppDispatch();
  const {
    registry,
    ready: registryReady,
    error: registryError
  } = useSelector((state: RootState) => state.registry);

  const userViews = useMemo(() => {
    return registry?.views ?? {} as Record<UserViewType, UserView[]>;
  }, [registry]);

  const userDashboards = useMemo(() => {
    const registryValue = registry?.registryValue;
    if (registryValue) {
      if (registryValue[DEPLOYMENT] ) {
        return registryValue[DEPLOYMENT] as { [dashboardId: string]: DashboardRegistryItem };
      } else if (Object.values(registryValue).every(isRegistryItem)) {
        return registryValue as { [dashboardId: string]: DashboardRegistryItem };
      }
    }
    return {};
  }, [registry]);

  const userHistory = useMemo(() => {
    return registry?.history ?? [];
  }, [registry]);

  // View Management

  const updateUserView = (type: UserViewType, view: UserView) => {
    if (!registry) return;
    const updated = structuredClone(registry);
    updated.views[type] = (updated.views[type] ?? []).map((v) => view.id === v.id ? view : v);
    dispatch(updateRegistry(updated));
  };

  const addUserView = (type: UserViewType, view: UserView) => {
    if (!registry) return;
    const updated = structuredClone(registry ?? { views: {}, registryValue: {}, history: [] });
    if (!updated.views[type]) {
      updated.views[type] = [];
    }
    updated.views[type].push(view);
    dispatch(updateRegistry(updated));
  };
  
  const deleteUserView = (type: UserViewType, viewId: number) => {
    if (!registry) return;
    const updated = structuredClone(registry);
    updated.views[type] = (updated.views?.[type] ?? []).filter((v) => v.id !== viewId);
    if (updated.views[type].length === 0) {
      updated.views[type] = [defaultViews[type]];
    }
    dispatch(updateRegistry(updated));
  };

  // View Filter Management

  const updateViewFilters = (type: UserViewType, id: UserView['id'], filters: UserView['filters']) => {
    if (!registry) return;
    const view = userViews[type]?.find((v) => v.id === id);
    if (view) {
      return updateUserView(type, { ...view, filters });
    }
  };

  const updateViewColumns = (type: UserViewType, id: UserView['id'], columns: UserView['columns']) => {
    if (!registry) return;
    const view = userViews[type]?.find((v) => v.id === id);
    if (view) {
      return updateUserView(type, { ...view, columns });
    }
  };

  const updateViewOrderBy = (type: UserViewType, id: UserView['id'], order: UserView['orderBy']) => {
    if (!registry) return;
    const view = userViews[type]?.find((v) => v.id === id);
    if (view) {
      return updateUserView(type, { ...view, orderBy: order });
    }
  };

  const updateViewUserOwnedOnly = (type: UserViewType, id: UserView['id'], userOwnedOnly: boolean) => {
    if (!registry) return;
    const view = userViews[type]?.find((v) => v.id === id);
    if (view) {
      return updateUserView(type, { ...view, userOwnedOnly });
    }
  };

  const updateViewColumn = (type: UserViewType, id: UserView['id'], columnId: string, property: string, value: any) => {
    if (!registry) return;
    const view = userViews[type]?.find((v) => v.id === id);
    if (view) {
      const updatedColumns = view.columns.map((col) =>
        col.id === columnId ? { ...col, [property]: value } : col,
      );
      return updateUserView(type, { ...view, columns: updatedColumns });
    }
  };

  const updateViewTableConfig = (type: UserViewType, id: UserView['id'], config: UserView['tableConfig']) => {
    if (!userViews[type]) return;
    const view = userViews[type]?.find((v) => v.id === id);
    if (view) {
      return updateUserView(type, { ...view, tableConfig: config });
    }
  };

  // Dashboards
  const updateUserDashboard = (dashboards: { [dashboardId: string]: DashboardRegistryItem }) => {
    if (!registry) return;
    const updated = structuredClone(registry);
    updated.registryValue[DEPLOYMENT] = dashboards;
    dispatch(updateRegistry(updated));
  };

  // History
  const updateUserHistory = (newHistory: RouteHistoryEntry[]) => {
    if (!registry) {
      return;
    }
    const updated = structuredClone(registry);
    updated.history = newHistory;
    dispatch(updateRegistry(updated));
  }

  // Notifications
  const updateUserNotifications = (notifications: Registry['notifications']) => {
    if (!registry) return;
    const updated = structuredClone(registry);
    updated.notifications = notifications;
    dispatch(updateRegistry(updated));
  }

  return {
    addUserView,
    updateUserView,
    deleteUserView,
    updateViewFilters,
    updateViewColumns,
    updateViewColumn,
    updateViewOrderBy,
    updateViewUserOwnedOnly,
    updateViewTableConfig,
    updateUserDashboard,
    updateUserHistory,
    updateUserNotifications,

    registry,
    registryReady,
    registryError,
    userViews,
    userDashboards,
    userHistory
  };
};